// @flow
import url from 'node:url';

import * as React from 'react';

import get from 'lodash/get';
import { useDispatch } from 'react-redux';

import { useHeadLink } from '@gandi/app-runner';
import { selectFromConfig } from '@gandi/redux-config';

import { useTranslator } from '@gandi/translate';

import { PasswordMeterField } from 'gandi.react-form';
import { POST } from 'gandi.redux-fetcher';

import { useAppSelector } from 'src/hooks/useAppSelector';

export type Props = {
  autoComplete?: string,
  inputs?: Array<string>,
  label?: string,
  name: string,
  onScoreChange?: (score: number) => void,
  required?: boolean,
  suggestions?: Array<string> | null,
  validate?: (fieldName: string) => void,
  placeholder?: string,
};

export const selectPasswordServiceURL: any = selectFromConfig<string>(
  'services.password',
  'https://pwd.gandi.net',
);

function PasswordChecker(props: Props): React.MixedElement {
  const dispatch = useDispatch();
  const translator = useTranslator();
  const { __ } = translator;

  const passwordService = useAppSelector(selectPasswordServiceURL);

  useHeadLink({
    rel: 'preconnect',
    href: passwordService,
    crossorigin: 'true',
  });

  const [suggestions, setSuggestions] = React.useState<Array<?string>>([]);
  const [warning, setWarning] = React.useState('');
  const [score, setScore] = React.useState(0);

  const translation = {
    // We don't translate some keys as the messages are redundant and always
    // show with each other.
    // Adapted from https://gitlab.corp.gandi.net/gandi-v5/gandi.account/blob/master/gandi/account/templates/base_layout.html#L105-145

    // Warnings
    'Straight rows of keys are easy to guess': '',
    'Short keyboard patterns are easy to guess': '',
    "Repeats like 'aaa' are easy to guess": '',
    "Repeats like 'abcabcabc' are only slightly harder to guess than 'abc'": '',
    'Sequences like abc or 6543 are easy to guess': '',
    'Recent years are easy to guess': '',
    'Dates are often easy to guess': '',
    'This is a top-10 common password': __('This is a top-10 common password.'),
    'This is a top-100 common password': __(
      'This is a top-100 common password.',
    ),
    'This is a very common password': __('This is a very common password.'),
    'This is similar to a commonly used password': __(
      'This is similar to a commonly used password.',
    ),
    'A word by itself is easy to guess': __(
      'A word by itself is easy to guess.',
    ),
    'Names and surnames by themselves are easy to guess': __(
      'Names and surnames by themselves are easy to guess.',
    ),
    'Common names and surnames are easy to guess': __(
      'Common names and surnames are easy to guess.',
    ),

    // Suggestions
    'Use a few words, avoid common phrases': __(
      'Use a few words, avoid common phrases.',
    ),
    'No need for symbols, digits, or uppercase letters': '',
    'Add another word or two. Uncommon words are better.': __(
      'Add another word or two. Uncommon words are better.',
    ),
    'Use a longer keyboard pattern with more turns': __(
      'Use a longer keyboard pattern with more turns.',
    ),
    'Avoid repeated words and characters': __(
      'Avoid repeated words and characters.',
    ),
    'Avoid sequences': __('Avoid sequences.'),
    'Avoid recent years': __('Avoid recent years.'),
    'Avoid years that are associated with you': __(
      'Avoid years that are associated with you.',
    ),
    'Avoid dates and years that are associated with you': __(
      'Avoid dates and years that are associated with you.',
    ),
    "Capitalization doesn't help very much": __(
      "Capitalization doesn't help very much.",
    ),
    'All-uppercase is almost as easy to guess as all-lowercase': __(
      'All-uppercase is almost as easy to guess as all-lowercase.',
    ),
    "Reversed words aren't much harder to guess": __(
      "Reversed words aren't much harder to guess.",
    ),
    "Predictable substitutions like '@' instead of 'a' don't help very much":
      __(
        "Predictable substitutions like '@' instead of 'a' don't help very much",
      ),
  };

  const handlePasswordCheck: (value: string) => Promise<void> = async (
    value: string,
  ) => {
    if (!value) {
      return;
    }

    const body = {
      password: value,
      user_inputs: props.inputs || [],
    };
    const options = {};

    try {
      const response = await dispatch(
        POST(url.resolve(passwordService, '/strength'), body, options),
      );

      // $FlowFixMe[prop-missing]
      // $FlowFixMe[incompatible-use]
      setScore(response.data.score);
      // $FlowFixMe[prop-missing]
      setWarning(translation[get(response.data, 'feedback.warning', '')]);
      setSuggestions(
        // $FlowFixMe[prop-missing]
        get(response.data, 'feedback.suggestions', []).map(s => translation[s]),
      );

      if (typeof props.onScoreChange === 'function') {
        props.onScoreChange(score);
      }
    } catch (err) {} // eslint-disable-line no-empty
  };
  return (
    <PasswordMeterField
      autoComplete={props.autoComplete}
      block
      handleCheck={handlePasswordCheck}
      inputs={props.inputs}
      labelText={props.label}
      name={props.name}
      required={props.required}
      score={score}
      suggestions={suggestions.length > 0 ? suggestions : props.suggestions}
      translator={translator}
      validate={props.validate}
      warning={warning}
      placeholder={props.placeholder}
    />
  );
}

export default PasswordChecker;
